import React, { FC, RefObject } from 'react';
import styled from 'styled-components';
import { ChevronIcon } from '../../../../widgets/icons/scene';
import { Tab, TabList, ZTabs } from '../../../../widgets/ztabs';
import { T } from '../../../../../shared/helpers';

// LA STRISCIA È UN GUSCIO, NON UNA SEZIONE. Qui dentro non si disegnano tessere, non si
// filtra e non si comanda niente: la sezione vera — quella del rail, con i suoi comandi, i
// suoi gruppi, le sue anteprime e il suo inspector — viene **spostata** dentro il corpo
// quando l'attracco cambia, e ci torna via quando si torna all'altro.
//
// Prima qui c'era una seconda implementazione dei materiali: una ricerca sua, un commutatore
// di vista suo e tessere che erano quadrati grigi. Il layout 2 non «mostrava i materiali in
// un altro modo» — perdeva la sezione (docs/scene-editor-step6-second-layout.md § 1).
//
// Del corpo resta il posto anche quando la striscia è chiusa: è lì che la sezione torna.

export interface LibraryCategory {
    tool: string;
    icon: React.ReactNode;
    count?: number;
    /** La categoria è dichiarata ma non ha ancora niente dietro: si vede spenta. */
    disabled?: boolean;
}

interface SceneEditorLibraryDrawerProps {
    categories: LibraryCategory[];
    activeTool: string;
    onSelectTool: (tool: string) => void;
    isOpen: boolean;
    onToggle: () => void;
    /** Il nodo in cui la sezione viene traslocata. Lo riempie chi possiede la sezione. */
    bodyRef: RefObject<HTMLDivElement | null>;
}

const LibraryRoot = styled.div`
    display: flex;
    flex-direction: column;
    height: 100%;
    min-height: 0;
    background: ${(props) => props.theme.app.backgroundColor};
`;

const LibraryHeader = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    min-height: 42px;
    padding: 0 10px;
    border-bottom: 1px solid ${(props) => props.theme.app.borderColor};
`;

const LibraryTabs = styled(ZTabs)`
    min-width: 0;

    .react-tabs__tab-list {
        margin: 0;
        background: transparent;
    }
`;

const LibraryTab = styled(Tab)`
    padding: 0 9px;
    font-size: 12px;

    svg {
        width: 16px;
        height: 16px;
    }
`;

const Count = styled.span`
    color: ${(props) => props.theme.app.primary};
    font-size: 11px;
`;

// Il verso del chevron è quello che il cassetto farà: giù per chiudersi, su per
// riaprirsi. Era un carattere tipografico — che cambia faccia con il font e non
// si allinea con gli altri glifi.
const LibraryToggle = styled.button<{ $open?: boolean }>`
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 28px;
    height: 28px;
    border: 0;
    border-radius: 6px;
    color: ${(props) => props.theme.app.textColor};
    background: transparent;

    svg {
        width: 16px;
        height: 16px;
        transform: rotate(${(props) => (props.$open ? '0deg' : '180deg')});
    }
    cursor: pointer;

    &:hover,
    &:focus-visible {
        color: ${(props) => props.theme.app.secondary};
        background: ${(props) => props.theme.app.pageBackgroundColor};
        outline: none;
    }
`;

// Chiusa, la striscia resta la sola testata: il corpo non si smonta — è dove la sezione
// vive — ma non deve occupare una riga di griglia.
const LibraryBody = styled.div<{ $open: boolean }>`
    display: ${(props) => (props.$open ? 'flex' : 'none')};
    flex: 1;
    min-height: 0;
    flex-direction: column;
`;

const SceneEditorLibraryDrawer: FC<SceneEditorLibraryDrawerProps> = ({ categories, activeTool, onSelectTool, isOpen, onToggle, bodyRef }) => {
    const activeIndex = Math.max(
        0,
        categories.findIndex((category) => category.tool === activeTool)
    );

    return (
        <LibraryRoot data-library-open={isOpen}>
            <LibraryHeader>
                <LibraryTabs
                    selectedIndex={activeIndex}
                    onSelect={(index: number) => {
                        const category = categories[index];
                        if (category && !category.disabled) onSelectTool(category.tool);
                        return !category?.disabled;
                    }}
                >
                    <TabList aria-label={T._('Asset library sections', 'Admin')}>
                        {categories.map((category) => (
                            <LibraryTab key={category.tool} disabled={category.disabled}>
                                {category.icon}
                                {category.tool} {category.count !== undefined && <Count>{category.count}</Count>}
                            </LibraryTab>
                        ))}
                    </TabList>
                </LibraryTabs>
                <LibraryToggle
                    type='button'
                    $open={isOpen}
                    onClick={onToggle}
                    aria-expanded={isOpen}
                    aria-label={isOpen ? T._('Collapse asset library', 'Admin') : T._('Expand asset library', 'Admin')}
                >
                    <ChevronIcon />
                </LibraryToggle>
            </LibraryHeader>
            <LibraryBody ref={bodyRef} $open={isOpen} />
        </LibraryRoot>
    );
};

export default SceneEditorLibraryDrawer;
